import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Heading,
  Alert,
  AlertIcon,
  Badge,
  SimpleGrid,
  Container,
  Spinner,
  useColorModeValue,
  Avatar,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Tooltip,
  Flex,
  Checkbox,
  Select,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import { SearchIcon, ViewIcon, DeleteIcon, SettingsIcon } from "@chakra-ui/icons";
import {
  getAllPersons,
  getPersonByIdentifier,
  getAllMeasurementsByIdentifier,
  authenticateVisaVita,
  isAuthenticated as isVisaVitaAuthenticated,
  getSubgroups,
  assignPatientsToSubgroup,
} from "api/visaVitaService";
import { DeletePatientDialog, SubgroupManagerModal, groupLabel } from "./PatientManagement";

const PatientsList = () => {
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [groupedPatients, setGroupedPatients] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [authenticationStatus, setAuthenticationStatus] = useState(null);
  const [visaVitaAuthStatus, setVisaVitaAuthStatus] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [availablePersons, setAvailablePersons] = useState([]);
  const [loadingPersons, setLoadingPersons] = useState(false);
  const [selectedTokenGroup, setSelectedTokenGroup] = useState('all');
  const [subgroups, setSubgroups] = useState([]);
  // 'all', 'none' (patients without a subgroup) or a subgroup id
  const [selectedSubgroup, setSelectedSubgroup] = useState('all');
  const [selectedPatientIds, setSelectedPatientIds] = useState([]);
  const [bulkSubgroupId, setBulkSubgroupId] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [isSubgroupManagerOpen, setIsSubgroupManagerOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);

  const history = useHistory();
  const toast = useToast();

  // Color mode values
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const tableHeaderHoverBg = useColorModeValue("gray.100", "gray.600");
  const tableRowHoverBg = useColorModeValue("gray.50", "gray.700");
  const purpleGradient = useColorModeValue(
    "linear(to-r, purple.400, purple.600)",
    "linear(to-r, purple.300, purple.500)"
  );

  // Extract identifiers from available persons
  const getIdentifierFromPerson = (person) => {
    let identifier = null;
    let source = null;

    if (person.mapping && person.mapping.externalIdentifier) {
      identifier = person.mapping.externalIdentifier;
      source = 'mapping.externalIdentifier';
    }
    else if (person.email && person.email.includes("@")) {
      const emailPrefix = person.email.split("@")[0];
      identifier = emailPrefix.replace(/_@_/g, "_0_");
      source = 'email';
    }
    else if (person.identifier) {
      identifier = person.identifier;
      source = 'identifier';
    }
    else if (person.publicToken) {
      identifier = person.publicToken;
      source = 'publicToken';
    }
    else {
      identifier = person.id?.toString();
      source = 'id';
    }

    return identifier;
  };

  // Fetch all available persons - SIMPLIFIED
  const fetchAllPersons = async () => {
    try {
      const result = await getAllPersons();
      if (result?.success && result.persons) {
        setAvailablePersons(result.persons);
        return result.persons;
      } else {
        return [];
      }
    } catch (error) {
      console.error('API error:', error.message);
      throw error;
    }
  };

  // Check authentication status
  const checkAuthenticationStatus = () => {
    const accessToken = localStorage.getItem("accessToken");
    const user = localStorage.getItem("user");
    const isMainAppAuthenticated = !!(accessToken && user);
    const isVisaVitaAuth = isVisaVitaAuthenticated();
    setAuthenticationStatus(isMainAppAuthenticated);
    setVisaVitaAuthStatus(isVisaVitaAuth);

    return isMainAppAuthenticated;
  };

  const ensureVisaVitaAuthentication = async () => {
    if (isVisaVitaAuthenticated()) {
      return true;
    }

    try {
      const username = localStorage.getItem("visaVitaUsername");
      const password = localStorage.getItem("visaVitaPassword");

      if (!username || !password) {
        throw new Error("No VisaVita credentials found");
      }

      await authenticateVisaVita(username, password);
      setVisaVitaAuthStatus(true);
      return true;
    } catch (error) {
      console.error('Auth failed:', error.message);
      throw error;
    }
  };

  // Set the patient list and its per-group counts
  // The latest list, so handlers that run back to back never work from a stale copy
  const patientsRef = useRef([]);

  const applyPatients = (patientList) => {
    patientsRef.current = patientList;
    setPatients(patientList);
    setGroupedPatients(patientList.reduce((groups, patient) => {
      const token = patient.publicToken || 'Unknown';
      if (!groups[token]) {
        groups[token] = [];
      }
      groups[token].push(patient);
      return groups;
    }, {}));
    setSelectedPatientIds(ids => ids.filter(id => patientList.some(patient => patient.patientId === id)));
  };

  const loadSubgroups = async () => {
    try {
      setSubgroups(await getSubgroups());
    } catch (error) {
      console.error('Error loading subgroups:', error.message);
      setSubgroups([]);
    }
  };

  // Reload subgroups and the patients' subgroup names after the subgroups were edited
  const handleSubgroupsChanged = async () => {
    const latest = await getSubgroups().catch(() => subgroups);
    setSubgroups(latest);
    const names = Object.fromEntries(latest.map(subgroup => [subgroup.id, subgroup.name]));
    applyPatients(patientsRef.current.map(patient => (
      patient.subgroupId && names[patient.subgroupId]
        ? { ...patient, subgroupName: names[patient.subgroupId] }
        : { ...patient, subgroupId: null, subgroupName: null }
    )));
    if (selectedSubgroup !== 'all' && selectedSubgroup !== 'none' && !names[selectedSubgroup]) {
      setSelectedSubgroup('all');
    }
  };

  // Put patients into a subgroup (null removes them from their subgroup)
  const handleAssignSubgroup = async (patientIds, subgroupId) => {
    setIsAssigning(true);
    try {
      await assignPatientsToSubgroup(patientIds, subgroupId);
      const subgroup = subgroups.find(s => s.id === subgroupId);
      const updated = patientsRef.current.map(patient => (
        patientIds.includes(patient.patientId)
          ? { ...patient, subgroupId: subgroup?.id ?? null, subgroupName: subgroup?.name || null }
          : patient
      ));
      applyPatients(updated);
      // Keep the subgroup counts in step without another request
      setSubgroups(current => current.map(s => ({
        ...s,
        patientCount: updated.filter(patient => patient.subgroupId === s.id).length
      })));
      toast({
        title: subgroup ? `Moved to ${subgroup.name}` : "Removed from subgroup",
        description: `${patientIds.length} patient(s) updated`,
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      toast({ title: "Could not update subgroup", description: error.message, status: "error", isClosable: true });
    } finally {
      setIsAssigning(false);
    }
  };

  const handlePatientDeleted = (deletedPatient) => {
    setPatientToDelete(null);
    applyPatients(patientsRef.current.filter(patient => patient.patientId !== deletedPatient.patientId));
    if (deletedPatient.subgroupId) {
      setSubgroups(current => current.map(s => (
        s.id === deletedPatient.subgroupId ? { ...s, patientCount: Math.max(0, s.patientCount - 1) } : s
      )));
    }
  };

  const togglePatientSelection = (patientId) => {
    setSelectedPatientIds(ids => ids.includes(patientId) ? ids.filter(id => id !== patientId) : [...ids, patientId]);
  };

  // Load all patients data
  const loadPatientsData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!checkAuthenticationStatus()) {
        throw new Error("Please sign in first");
      }

      const visaVitaAuth = await ensureVisaVitaAuthentication();
      if (!visaVitaAuth) {
        throw new Error("VisaVita auth failed");
      }

      const persons = await fetchAllPersons();
      if (!persons || persons.length === 0) {
        throw new Error("No persons found");
      }
      const simplePatients = persons.map((person, index) => ({
        id: person.mapping?.id || index,
        name: `Patient ${person.mapping?.externalIdentifier || index + 1}`,
        patientId: person.mapping?.externalIdentifier || 'N/A',
        dateCreated: person.mapping?.dateCreated || 'N/A',
        lastUpdated: person.mapping?.lastUpdated || 'N/A',
        personId: person.mapping?.personId || index,
        publicToken: person.publicToken || 'Unknown',
        subgroupId: person.subgroup?.id ?? null,
        subgroupName: person.subgroup?.name || null
      }));

      applyPatients(simplePatients);
      setFilteredPatients(simplePatients);
      await loadSubgroups();
      const publicTokens = [...new Set(persons.map(p => p.publicToken))];

      toast({
        title: "Patients Loaded Successfully",
        description: `Loaded ${simplePatients.length} patients with ${publicTokens.length} token types: ${publicTokens.join(', ')}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      setError(`Error: ${error.message}`);
      toast({
        title: "Error Loading Patients",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPatientsData();
  }, []);

  useEffect(() => {
    let filtered = patients;
    if (selectedTokenGroup !== 'all') {
      filtered = patients.filter(patient => patient.publicToken === selectedTokenGroup);
      if (selectedSubgroup === 'none') {
        filtered = filtered.filter(patient => !patient.subgroupId);
      } else if (selectedSubgroup !== 'all') {
        filtered = filtered.filter(patient => patient.subgroupId === selectedSubgroup);
      }
    }

    if (searchTerm !== "") {
      filtered = filtered.filter(patient =>
        patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.patientId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.id.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (sortKey) {
      filtered = [...filtered].sort((a, b) => {
        let aVal = a[sortKey];
        let bVal = b[sortKey];

        if (sortKey === 'dateCreated' || sortKey === 'lastUpdated') {
          aVal = aVal === 'N/A' ? new Date(0) : new Date(aVal);
          bVal = bVal === 'N/A' ? new Date(0) : new Date(bVal);
        } else if (sortKey === 'id') {
          aVal = parseInt(aVal) || 0;
          bVal = parseInt(bVal) || 0;
        } else {
          aVal = aVal?.toString().toLowerCase() || '';
          bVal = bVal?.toString().toLowerCase() || '';
        }
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    setFilteredPatients(filtered);
  }, [searchTerm, patients, sortKey, sortDirection, selectedTokenGroup, selectedSubgroup]);

  // Subgroups and bulk selections belong to one group
  useEffect(() => {
    setSelectedSubgroup('all');
    setSelectedPatientIds([]);
    setBulkSubgroupId('');
  }, [selectedTokenGroup]);

  const groupSubgroups = subgroups.filter(subgroup => subgroup.group === selectedTokenGroup);
  const visibleSelectedIds = selectedPatientIds.filter(id => filteredPatients.some(patient => patient.patientId === id));
  const allVisibleSelected = filteredPatients.length > 0 && visibleSelectedIds.length === filteredPatients.length;

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (key) => {
    if (sortKey !== key) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const handleViewDetails = (patientId) => {
    if (patientId) {
      history.push(`/admin/patient-details/${patientId}`);
    } else {
      history.push(`/admin/patient-details`);
    }

    toast({
      title: "Loading Patient Details",
      description: patientId ? `Viewing details for patient ${patientId}` : "Loading patient details",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  // Get avatar color for patient
  const getAvatarColor = () => {
    return "purple";
  };

  // Get color for public token badges
  const getTokenColor = (token) => {
    switch (token) {
      case 'VISAVITA_GLUCOSE':
        return 'blue';
      case 'VISAVITA_STRESS':
        return 'red';
      case 'VISAVITA_CORSANO':
        return 'green';
      default:
        return 'gray';
    }
  };

  // Show authentication required state
  if (authenticationStatus === false) {
    return (
      <Container maxW="full" py={8} bg={bgColor} minH="100vh">
        <VStack spacing={6} align="center">
          <Text fontSize="6xl" color="red.500">⚠️</Text>
          <VStack spacing={2} textAlign="center">
            <Heading size="lg" color="red.500">Authentication Required</Heading>
            <Text color="gray.600">
              Please sign in to access patient data. You need to be authenticated to view this page.
            </Text>
          </VStack>
          <Alert status="warning" maxW="md">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Access Denied</Text>
              <Text fontSize="sm">
                This page requires authentication. Please go to the Sign In page and log in with your credentials.
              </Text>
            </Box>
          </Alert>
        </VStack>
      </Container>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Container maxW="full" py={8} bg={bgColor} minH="100vh">
        <VStack spacing={8} align="center" justify="center" minH="60vh">
          <Spinner size="xl" color="purple.500" thickness="4px" />
          <VStack spacing={2}>
            <Heading size="lg" color="purple.500">Loading Patients</Heading>
            <Text color="gray.600">Fetching patient data from Visa Vita...</Text>
          </VStack>
        </VStack>
      </Container>
    );
  }

  // Error state
  if (error && patients.length === 0) {
    return (
      <Container maxW="full" py={8} bg={bgColor} minH="100vh">
        <VStack spacing={6} align="stretch">
          <Alert status="error">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Failed to Load Patients</Text>
              <Text fontSize="sm">{error}</Text>
            </Box>
          </Alert>
          <Button
            colorScheme="purple"
            onClick={loadPatientsData}
            isLoading={isLoading}
          >
            Retry Loading Data
          </Button>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="full" py={8} bg={bgColor} minH="100vh">
      <VStack spacing={8} align="stretch">
        {/* Header Section */}
        <Box>
          <HStack justify="space-between" align="center" mb={6} mt={10}>
            <VStack align="start" spacing={1}>
              <Heading
                size="xl"
                bgGradient={purpleGradient}
                bgClip="text"
              >
                Patients List
              </Heading>
              <Text color="gray.600" fontSize="lg">
                Live patient data from VisaVita
              </Text>
              {availablePersons.length > 0 && (
                <Text color="gray.500" fontSize="sm">
                  {availablePersons.length} persons found in system
                </Text>
              )}
            </VStack>

            <Button
              colorScheme="purple"
              variant="outline"
              onClick={loadPatientsData}
              isLoading={isLoading || loadingPersons}
              loadingText="Loading"
              size="md"
            >
              Refresh Data
            </Button>
          </HStack>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert status="warning">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Warning</Text>
              <Text fontSize="sm">{error}</Text>
            </Box>
          </Alert>
        )}

        {/* Search Section with Filter Display */}
        <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
          {/* Active Filter Display */}
          {selectedTokenGroup !== 'all' && (
            <HStack justify="space-between" mb={4} p={3} bg={useColorModeValue("gray.50", "gray.700")} borderRadius="md">
              <HStack spacing={3}>
                <Text fontSize="sm" color="gray.600">
                  Filtered by:
                </Text>
                <Badge colorScheme={getTokenColor(selectedTokenGroup)} variant="solid" px={3} py={1}>
                  {selectedTokenGroup}
                </Badge>
              </HStack>
              <Button size="xs" variant="ghost" onClick={() => setSelectedTokenGroup('all')}>
                Clear Filter
              </Button>
            </HStack>
          )}

          <Flex direction={{ base: "column", md: "row" }} gap={4} align="center">
            <Box flex="1" maxW="md">
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search patients by name or Patient ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  bg="white"
                  borderColor={borderColor}
                  _focus={{ borderColor: "purple.500", boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)" }}
                />
              </InputGroup>
            </Box>

            <HStack spacing={2}>
              <Text fontSize="sm" color="gray.600">
                {filteredPatients.length} of {patients.length} patients
                {selectedTokenGroup !== 'all' && ` in ${selectedTokenGroup}`}
              </Text>
            </HStack>
          </Flex>
        </Box>

        {/* Stats Section - Total Patients + VisaVita Token Groups */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          {/* TOTAL PATIENTS */}
          <Box
            bg={cardBg}
            shadow="md"
            borderRadius="lg"
            p={6}
            textAlign="center"
            cursor="pointer"
            border="2px solid transparent"
            _hover={{ borderColor: "purple.300", transform: "translateY(-2px)" }}
            transition="all 0.2s"
            onClick={() => setSelectedTokenGroup('all')}
            borderColor={selectedTokenGroup === 'all' ? "purple.500" : "transparent"}
          >
            <Badge colorScheme="purple" variant="solid" mb={3} px={3} py={1}>
              TOTAL
            </Badge>
            <Text fontSize="2xl" fontWeight="bold" color="purple.500">
              {patients.length}
            </Text>
            <Text color="gray.600" fontSize="sm">Total Patients</Text>
          </Box>

          {/* VISAVITA_GLUCOSE */}
          <Box
            bg={cardBg}
            shadow="md"
            borderRadius="lg"
            p={6}
            textAlign="center"
            cursor="pointer"
            border="2px solid transparent"
            _hover={{ borderColor: "blue.300", transform: "translateY(-2px)" }}
            transition="all 0.2s"
            onClick={() => setSelectedTokenGroup(selectedTokenGroup === 'VISAVITA_GLUCOSE' ? 'all' : 'VISAVITA_GLUCOSE')}
            borderColor={selectedTokenGroup === 'VISAVITA_GLUCOSE' ? "blue.500" : "transparent"}
          >
            <Badge colorScheme="blue" variant="solid" mb={3} px={3} py={1}>
              VISAVITA_GLUCOSE
            </Badge>
            <Text fontSize="2xl" fontWeight="bold" color="blue.500">
              {groupedPatients['VISAVITA_GLUCOSE']?.length || 0}
            </Text>
            <Text color="gray.600" fontSize="sm">Glucose Patients</Text>
          </Box>

          {/* VISAVITA_STRESS */}
          <Box
            bg={cardBg}
            shadow="md"
            borderRadius="lg"
            p={6}
            textAlign="center"
            cursor="pointer"
            border="2px solid transparent"
            _hover={{ borderColor: "red.300", transform: "translateY(-2px)" }}
            transition="all 0.2s"
            onClick={() => setSelectedTokenGroup(selectedTokenGroup === 'VISAVITA_STRESS' ? 'all' : 'VISAVITA_STRESS')}
            borderColor={selectedTokenGroup === 'VISAVITA_STRESS' ? "red.500" : "transparent"}
          >
            <Badge colorScheme="red" variant="solid" mb={3} px={3} py={1}>
              VISAVITA_STRESS
            </Badge>
            <Text fontSize="2xl" fontWeight="bold" color="red.500">
              {groupedPatients['VISAVITA_STRESS']?.length || 0}
            </Text>
            <Text color="gray.600" fontSize="sm">Stress Patients</Text>
          </Box>

          {/* VISAVITA_CORSANO */}
          <Box
            bg={cardBg}
            shadow="md"
            borderRadius="lg"
            p={6}
            textAlign="center"
            cursor="pointer"
            border="2px solid transparent"
            _hover={{ borderColor: "green.300", transform: "translateY(-2px)" }}
            transition="all 0.2s"
            onClick={() => setSelectedTokenGroup(selectedTokenGroup === 'VISAVITA_CORSANO' ? 'all' : 'VISAVITA_CORSANO')}
            borderColor={selectedTokenGroup === 'VISAVITA_CORSANO' ? "green.500" : "transparent"}
          >
            <Badge colorScheme="green" variant="solid" mb={3} px={3} py={1}>
              VISAVITA_CORSANO
            </Badge>
            <Text fontSize="2xl" fontWeight="bold" color="green.500">
              {groupedPatients['VISAVITA_CORSANO']?.length || 0}
            </Text>
            <Text color="gray.600" fontSize="sm">Cursano Patients</Text>
          </Box>
        </SimpleGrid>

        {/* Subgroups of the selected group */}
        {selectedTokenGroup !== 'all' && (
          <Box bg={cardBg} shadow="md" borderRadius="lg" p={5}>
            <HStack justify="space-between" align="start" mb={3} wrap="wrap" rowGap={2}>
              <VStack align="start" spacing={0}>
                <Text fontWeight="bold" color={`${getTokenColor(selectedTokenGroup)}.500`}>
                  {groupLabel(selectedTokenGroup)} subgroups
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Filter the list by subgroup. Assign patients with the Subgroup column or by selecting several rows.
                </Text>
              </VStack>
              <Button size="sm" leftIcon={<SettingsIcon />} variant="outline" colorScheme="purple" onClick={() => setIsSubgroupManagerOpen(true)}>
                Manage subgroups
              </Button>
            </HStack>
            <Wrap spacing={2}>
              <WrapItem>
                <Button size="sm" variant={selectedSubgroup === 'all' ? 'solid' : 'outline'} colorScheme="purple" onClick={() => setSelectedSubgroup('all')}>
                  All ({groupedPatients[selectedTokenGroup]?.length || 0})
                </Button>
              </WrapItem>
              {groupSubgroups.map(subgroup => (
                <WrapItem key={subgroup.id}>
                  <Tooltip label={subgroup.description || ''} isDisabled={!subgroup.description} hasArrow>
                    <Button
                      size="sm"
                      variant={selectedSubgroup === subgroup.id ? 'solid' : 'outline'}
                      colorScheme={getTokenColor(selectedTokenGroup)}
                      onClick={() => setSelectedSubgroup(subgroup.id)}
                    >
                      {subgroup.name} ({subgroup.patientCount})
                    </Button>
                  </Tooltip>
                </WrapItem>
              ))}
              <WrapItem>
                <Button size="sm" variant={selectedSubgroup === 'none' ? 'solid' : 'outline'} colorScheme="gray" onClick={() => setSelectedSubgroup('none')}>
                  No subgroup ({(groupedPatients[selectedTokenGroup] || []).filter(patient => !patient.subgroupId).length})
                </Button>
              </WrapItem>
            </Wrap>

            {visibleSelectedIds.length > 0 && (
              <HStack mt={4} p={3} bg={tableHeaderBg} borderRadius="md" spacing={3} wrap="wrap" rowGap={2}>
                <Text fontSize="sm" fontWeight="medium">{visibleSelectedIds.length} selected</Text>
                <Select
                  size="sm"
                  width="220px"
                  placeholder="Choose subgroup"
                  value={bulkSubgroupId}
                  onChange={(e) => setBulkSubgroupId(e.target.value)}
                >
                  {groupSubgroups.map(subgroup => (
                    <option key={subgroup.id} value={subgroup.id}>{subgroup.name}</option>
                  ))}
                </Select>
                <Button
                  size="sm"
                  colorScheme="purple"
                  isDisabled={!bulkSubgroupId}
                  isLoading={isAssigning}
                  onClick={() => handleAssignSubgroup(visibleSelectedIds, Number(bulkSubgroupId))}
                >
                  Move to subgroup
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  isLoading={isAssigning}
                  onClick={() => handleAssignSubgroup(visibleSelectedIds, null)}
                >
                  Remove from subgroup
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedPatientIds([])}>
                  Clear selection
                </Button>
              </HStack>
            )}
          </Box>
        )}

        {/* Patients Table */}
        <Box bg={cardBg} shadow="md" borderRadius="lg" overflow="hidden">
          <Box>
            <Table variant="simple" size="md" width="100%" sx={{ "th, td": { whiteSpace: "normal", wordBreak: "break-word" } }}>
              <Thead bg={tableHeaderBg}>
                <Tr>
                  {selectedTokenGroup !== 'all' && (
                    <Th py={4} pl={3} pr={0} width="1%">
                      <Checkbox
                        colorScheme="purple"
                        isChecked={allVisibleSelected}
                        isIndeterminate={visibleSelectedIds.length > 0 && !allVisibleSelected}
                        onChange={() => setSelectedPatientIds(allVisibleSelected ? [] : filteredPatients.map(patient => patient.patientId))}
                        aria-label="Select all patients shown"
                      />
                    </Th>
                  )}
                  <Th
                    py={4} px={3} fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider"
                    cursor="pointer"
                    _hover={{ bg: tableHeaderHoverBg }}
                    onClick={() => handleSort('name')}
                  >
                    Patient Information{getSortIndicator('name')}
                  </Th>
                  <Th
                    py={4} px={3} fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider"
                    cursor="pointer"
                    _hover={{ bg: tableHeaderHoverBg }}
                    onClick={() => handleSort('patientId')}
                    display={{ base: "none", md: "table-cell" }}
                  >
                    Patient ID{getSortIndicator('patientId')}
                  </Th>
                  <Th
                    py={4} px={3} fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider"
                    cursor="pointer"
                    _hover={{ bg: tableHeaderHoverBg }}
                    onClick={() => handleSort('subgroupName')}
                  >
                    Subgroup{getSortIndicator('subgroupName')}
                  </Th>
                  <Th
                    py={4} px={3} fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider"
                    cursor="pointer"
                    _hover={{ bg: tableHeaderHoverBg }}
                    onClick={() => handleSort('dateCreated')}
                    display={{ base: "none", "2xl": "table-cell" }}
                  >
                    Date Created{getSortIndicator('dateCreated')}
                  </Th>
                  <Th
                    py={4} px={3} fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider"
                    cursor="pointer"
                    _hover={{ bg: tableHeaderHoverBg }}
                    onClick={() => handleSort('lastUpdated')}
                    display={{ base: "none", "2xl": "table-cell" }}
                  >
                    Last Updated{getSortIndicator('lastUpdated')}
                  </Th>
                  <Th py={4} px={3} fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                    Actions
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredPatients.length > 0 ? (
                  filteredPatients.map((patient, index) => (
                    <Tr
                      key={patient.id}
                      _hover={{ bg: tableRowHoverBg }}
                      borderBottom="1px"
                      borderColor={borderColor}
                    >
                      {selectedTokenGroup !== 'all' && (
                        <Td py={4} pl={3} pr={0}>
                          <Checkbox
                            colorScheme="purple"
                            isChecked={selectedPatientIds.includes(patient.patientId)}
                            onChange={() => togglePatientSelection(patient.patientId)}
                            aria-label={`Select ${patient.patientId}`}
                          />
                        </Td>
                      )}
                      <Td py={4} px={3}>
                        <HStack spacing={3}>
                          <Avatar
                            name={patient.name}
                            size="sm"
                            bg={`${getAvatarColor()}.500`}
                            color="white"
                          />
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="semibold" fontSize="sm">{patient.name}</Text>
                            <Badge size="sm" colorScheme={getTokenColor(patient.publicToken)} variant="outline">
                              {patient.publicToken}
                            </Badge>
                          </VStack>
                        </HStack>
                      </Td>

                      <Td py={4} px={3} display={{ base: "none", md: "table-cell" }}>
                        <Text fontFamily="mono" fontSize="sm" fontWeight="medium">
                          {patient.patientId}
                        </Text>
                      </Td>

                      <Td py={4} px={3}>
                        {subgroups.some(subgroup => subgroup.group === patient.publicToken) ? (
                          <Select
                            size="sm"
                            minW="130px"
                            value={patient.subgroupId ?? ''}
                            onChange={(e) => handleAssignSubgroup([patient.patientId], e.target.value ? Number(e.target.value) : null)}
                            isDisabled={isAssigning}
                          >
                            <option value="">No subgroup</option>
                            {subgroups.filter(subgroup => subgroup.group === patient.publicToken).map(subgroup => (
                              <option key={subgroup.id} value={subgroup.id}>{subgroup.name}</option>
                            ))}
                          </Select>
                        ) : (
                          <Text fontSize="sm" color="gray.400">–</Text>
                        )}
                      </Td>

                      <Td py={4} px={3} display={{ base: "none", "2xl": "table-cell" }}>
                        <Text fontSize="sm">
                          {patient.dateCreated !== 'N/A'
                            ? new Date(patient.dateCreated).toLocaleDateString()
                            : 'N/A'
                          }
                        </Text>
                      </Td>

                      <Td py={4} px={3} display={{ base: "none", "2xl": "table-cell" }}>
                        <Text fontSize="sm">
                          {patient.lastUpdated !== 'N/A'
                            ? new Date(patient.lastUpdated).toLocaleDateString()
                            : 'N/A'
                          }
                        </Text>
                      </Td>

                      <Td py={4} px={3}>
                        <HStack spacing={2}>
                          <Tooltip label="View Patient Details" hasArrow>
                            <IconButton
                              icon={<ViewIcon />}
                              aria-label="View Details"
                              size="sm"
                              colorScheme="purple"
                              variant="ghost"
                              onClick={() => handleViewDetails(patient.patientId)}
                              _hover={{ bg: "purple.100" }}
                            />
                          </Tooltip>
                          <Tooltip label="Delete Patient" hasArrow>
                            <IconButton
                              icon={<DeleteIcon />}
                              aria-label="Delete Patient"
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => setPatientToDelete(patient)}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={selectedTokenGroup !== 'all' ? 7 : 6} py={12} textAlign="center">
                      <VStack spacing={3}>
                        <Text fontSize="lg" color="gray.500" fontWeight="medium">
                          {searchTerm ? "No patients found" : "No patient data available"}
                        </Text>
                        <Text fontSize="sm" color="gray.400">
                          {searchTerm
                            ? `No results found for "${searchTerm}". Try adjusting your search.`
                            : "Patient data will appear here once loaded from Visa Vita."
                          }
                        </Text>
                      </VStack>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </Box>
      </VStack>

      {selectedTokenGroup !== 'all' && (
        <SubgroupManagerModal
          isOpen={isSubgroupManagerOpen}
          onClose={() => setIsSubgroupManagerOpen(false)}
          group={selectedTokenGroup}
          subgroups={subgroups}
          onChanged={handleSubgroupsChanged}
        />
      )}

      <DeletePatientDialog
        patient={patientToDelete}
        onClose={() => setPatientToDelete(null)}
        onDeleted={handlePatientDeleted}
      />
    </Container>
  );
};

export default PatientsList; 